import {
  PageBlueprint,
  coreExtensionData,
  createExtensionInput,
  createFrontendModule,
} from '@backstage/frontend-plugin-api'
import {
  CatalogFilterBlueprint,
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha'
import catalogPlugin from '@backstage/plugin-catalog/alpha'
import { Entity } from '@backstage/catalog-model'
import {
  CORPORATE_KINDS,
  DG_ANNOTATIONS,
  PROMETHEUS_HOST_ANNOTATION,
} from '@internal/backstage-plugin-dg-catalog-common'
import { RiApps2Line } from '@remixicon/react'
import { hasEnvironments } from './hooks'

const isCorporateKind = (entity: Entity) =>
  CORPORATE_KINDS.includes(entity.kind.toLocaleLowerCase('en-US'))

const isDeployedComponent = (entity: Entity) =>
  entity.kind.toLocaleLowerCase('en-US') === 'component' &&
  hasEnvironments(entity)

/** Resource hosted on a droplet that node_exporter reports on (see catalog/resources.yaml). */
const hasPrometheusHost = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[PROMETHEUS_HOST_ANNOTATION])

const corporateCard = EntityCardBlueprint.make({
  name: 'dg-corporate',
  params: {
    type: 'info',
    filter: isCorporateKind,
    loader: async () => {
      const { CorporateCard } = await import('./components/CorporateCard')
      return <CorporateCard />
    },
  },
})

const completenessCard = EntityCardBlueprint.make({
  name: 'dg-completeness',
  params: {
    type: 'info',
    filter: isCorporateKind,
    loader: async () => {
      const { CompletenessCard } = await import('./components/CompletenessCard')
      return <CompletenessCard />
    },
  },
})

const alertsCard = EntityCardBlueprint.make({
  name: 'dg-alerts',
  params: {
    type: 'info',
    filter: isDeployedComponent,
    loader: async () => {
      const { AlertsCard } = await import('./components/AlertsCard')
      return <AlertsCard />
    },
  },
})

const resourcesCard = EntityCardBlueprint.make({
  name: 'dg-resources',
  params: {
    type: 'content',
    filter: isDeployedComponent,
    loader: async () => {
      const { ResourcesCard } = await import('./components/ResourcesCard')
      return <ResourcesCard />
    },
  },
})

const observedDependenciesCard = EntityCardBlueprint.make({
  name: 'dg-observed-dependencies',
  params: {
    type: 'content',
    filter: isDeployedComponent,
    loader: async () => {
      const { ObservedDependenciesCard } = await import(
        './components/ObservedDependenciesCard'
      )
      return <ObservedDependenciesCard />
    },
  },
})

const scaleCard = EntityCardBlueprint.make({
  name: 'dg-scale',
  params: {
    type: 'content',
    filter: isDeployedComponent,
    loader: async () => {
      const { ScaleCard } = await import('./components/ScaleCard')
      return <ScaleCard />
    },
  },
})

const gitlabCard = EntityCardBlueprint.make({
  name: 'dg-gitlab',
  params: {
    type: 'content',
    filter: (entity: Entity) =>
      Boolean(entity.metadata.annotations?.['gitlab.com/project-slug']),
    loader: async () => {
      const { GitLabCard } = await import('./components/GitLabCard')
      return <GitLabCard />
    },
  },
})

const securityCard = EntityCardBlueprint.make({
  name: 'dg-security',
  params: {
    type: 'content',
    filter: isDeployedComponent,
    loader: async () => {
      const { SecurityCard } = await import('./components/SecurityCard')
      return <SecurityCard />
    },
  },
})

const vaultSecretsCard = EntityCardBlueprint.make({
  name: 'dg-vault-secrets',
  params: {
    type: 'content',
    filter: (entity: Entity) =>
      Boolean(entity.metadata.annotations?.[DG_ANNOTATIONS.vaultSecrets]),
    loader: async () => {
      const { VaultSecretsCard } = await import('./components/VaultSecretsCard')
      return <VaultSecretsCard />
    },
  },
})

const clusterCard = EntityCardBlueprint.make({
  name: 'dg-cluster',
  params: {
    type: 'content',
    filter: (entity: Entity) =>
      entity.kind.toLocaleLowerCase('en-US') === 'resource' &&
      entity.spec?.type === 'kubernetes-cluster',
    loader: async () => {
      const { ClusterResourceCard } = await import('./components/InfraCards')
      return <ClusterResourceCard />
    },
  },
})

const dropletCard = EntityCardBlueprint.make({
  name: 'dg-droplet',
  params: {
    type: 'content',
    filter: hasPrometheusHost,
    loader: async () => {
      const { DropletResourceCard } = await import('./components/InfraCards')
      return <DropletResourceCard />
    },
  },
})

const metricsContent = EntityContentBlueprint.make({
  name: 'dg-metrics',
  params: {
    path: 'metrics',
    title: 'Metrics',
    group: 'observability',
    filter: isDeployedComponent,
    loader: async () => {
      const { GrafanaMetricsContent } = await import(
        './components/GrafanaMetricsContent'
      )
      return <GrafanaMetricsContent />
    },
  },
})

const dropletMetricsContent = EntityContentBlueprint.make({
  name: 'dg-droplet-metrics',
  params: {
    path: 'metrics',
    title: 'Metrics',
    group: 'observability',
    filter: hasPrometheusHost,
    loader: async () => {
      const { DropletMetricsContent } = await import(
        './components/DropletMetricsContent'
      )
      return <DropletMetricsContent />
    },
  },
})

const logsContent = EntityContentBlueprint.make({
  name: 'dg-logs',
  params: {
    path: 'logs',
    title: 'Logs',
    group: 'observability',
    filter: isDeployedComponent,
    loader: async () => {
      const { LogsContent } = await import('./components/LogsContent')
      return <LogsContent />
    },
  },
})

const completenessFilter = CatalogFilterBlueprint.make({
  name: 'dg-completeness',
  params: {
    loader: async () => {
      const { CompletenessPicker } = await import(
        './components/CompletenessFilter'
      )
      return <CompletenessPicker />
    },
  },
})

// Same id as the catalog plugin's page:catalog, so this replaces it; needed to add a table column.
const catalogIndexPage = PageBlueprint.makeWithOverrides({
  inputs: {
    filters: createExtensionInput([coreExtensionData.reactElement]),
  },
  factory(originalFactory, { inputs }) {
    return originalFactory({
      path: '/catalog',
      routeRef: catalogPlugin.routes.catalogIndex,
      icon: <RiApps2Line size="1em" />,
      title: 'Catalog',
      loader: async () => {
        const { DgCatalogPage } = await import('./components/DgCatalogPage')
        const filters = inputs.filters.map(f =>
          f.get(coreExtensionData.reactElement),
        )
        return <DgCatalogPage filters={<>{filters}</>} />
      },
    })
  },
})

export const dgCatalogModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    corporateCard,
    completenessCard,
    alertsCard,
    resourcesCard,
    observedDependenciesCard,
    scaleCard,
    gitlabCard,
    securityCard,
    vaultSecretsCard,
    clusterCard,
    dropletCard,
    metricsContent,
    dropletMetricsContent,
    logsContent,
    completenessFilter,
    catalogIndexPage,
  ],
})
